import type Luminary from '@lumen5/luminary';
import type { SceneGroup } from '@lumen5/luminary';

import { $matchAllBoundaryNodes } from './lexical-boundary';
import { partitionTranscriptByScene } from './transcript-partition';
import { getInterleavedTranscript, interleavedTokenToString } from './transcript-tokens';

import { getEditorFromSceneGroupId } from 'js/src/data/transcript';
import { $getTimeIndicatedBy } from 'js/src/data/transcript/transcript-operations';
import { checkpointVideo } from 'js/src/data/video-data';
import uuid from 'js/src/libs/uuid';
import { saveRawLuminary } from 'js/src/luminary';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import {
    moveOverlaysAwayFromTimesOperation,
    translateOverlayEndOperation,
    translateOverlayStartOperation,
} from 'js/src/luminary/luminary-operations';
import { getBaseSceneDesignId } from 'js/src/luminary/luminary-selectors';
import { runVoicedrivenTranscriptBatchOperation } from 'js/src/luminary/voicedriven-scenes';

/**
 * How many requested words a result echoes in full before eliding the middle. A full-video
 * treatment (e.g. an audiogram) resolves to every token of the transcript (add_voiceover_scene's
 * cover_whole_group reconstructs them), so echoing the list back verbatim would repeat the whole
 * transcript on the longest call there is.
 */
const MAX_ECHOED_WORDS = 12;
const ECHOED_EDGE_WORDS = 3;

/** The covered range as the agent should read it back: the words themselves, elided when long. */
export function describeCoveredWords(words: string[]): string {
    if (words.length <= MAX_ECHOED_WORDS) {
        return `"${words.join(' ')}"`;
    }
    const start = words.slice(0, ECHOED_EDGE_WORDS).join(' ');
    const end = words.slice(-ECHOED_EDGE_WORDS).join(' ');
    return `"${start} … ${end}" (${words.length} words)`;
}

export type OverlayWordRange =
    | { startTimeMillis: number; endTimeMillis: number }
    | { error: string };

/**
 * Resolve a sequence of transcript words to the transcript-time range they occupy, via the same
 * lexical helper the UI uses (`$getTimeIndicatedBy`), so an agent-declared range snaps to the same
 * boundaries a word selection would. This is the placement math WITHOUT the overlap rejection: shared
 * by add_voiceover_scene (which rejects collisions via {@link
 * resolveOverlayRangeOperation}) and set_scene_range (which re-times an overlay and must not reject
 * overlapping itself). `requestedWords` may include `<pause seconds="N"/>` tags; returns `{ error }`
 * on any problem.
 *
 * The words may occur more than once in the transcript. By default the first occurrence wins;
 * `isPreferredRange` lets a caller scoped to part of the transcript (e.g. one scene) take the
 * first occurrence it accepts instead, falling back to the first occurrence when none qualifies so
 * the caller's own range check can explain the miss.
 */
export const resolveOverlayWordRangeOperation = createLuminaryOperation(
    (
        { getState, call },
        sceneGroup: SceneGroup,
        requestedWords: string[],
        isPreferredRange?: (range: { startTimeMillis: number; endTimeMillis: number }) => boolean,
    ): OverlayWordRange => {
        if (requestedWords.length === 0) {
            return { error: 'words array cannot be empty. Please provide at least one word.' };
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) {
            return { error: 'The transcript editor for this scene group is not available.' };
        }

        return editor.getEditorState().read((): OverlayWordRange => {
            const nodeSpans = $matchAllBoundaryNodes(requestedWords);
            if ('error' in nodeSpans) {
                return nodeSpans;
            }
            const toRange = (nodeSpan: (typeof nodeSpans)[number]) => {
                const { startTime, endTime } = call(
                    $getTimeIndicatedBy,
                    nodeSpan.firstNode,
                    nodeSpan.lastNode,
                    sceneGroup.id,
                );
                return { startTimeMillis: startTime, endTimeMillis: endTime };
            };
            if (isPreferredRange) {
                for (const nodeSpan of nodeSpans) {
                    const range = toRange(nodeSpan);
                    if (isPreferredRange(range)) {
                        return range;
                    }
                }
            }
            return toRange(nodeSpans[0]);
        });
    },
);

/**
 * {@link resolveOverlayWordRangeOperation} plus rejection of ranges that collide with an existing
 * overlay, for placing a NEW overlay (add_voiceover_scene). Overlap ranges come
 * from the shared partition, so this agrees with the coverage the agent sees.
 */
export const resolveOverlayRangeOperation = createLuminaryOperation(
    ({ getState, call }, sceneGroup: SceneGroup, requestedWords: string[]): OverlayWordRange => {
        const range = call(resolveOverlayWordRangeOperation, sceneGroup, requestedWords);
        if ('error' in range) {
            return range;
        }
        const { startTimeMillis, endTimeMillis } = range;

        const partition = partitionTranscriptByScene(getState(), sceneGroup);
        const existingOverlayRanges = (partition ?? []).filter((entry) => entry.role === 'overlay');
        for (const overlay of existingOverlayRanges) {
            const hasOverlap = !(
                endTimeMillis <= overlay.startMillis || startTimeMillis >= overlay.endMillis
            );
            if (hasOverlap) {
                const affectedWords = getInterleavedTranscript(getState(), sceneGroup)
                    .filter(
                        (token) =>
                            !(
                                token.endTime <= overlay.startMillis ||
                                token.startTime >= overlay.endMillis
                            ),
                    )
                    .map(interleavedTokenToString);
                return {
                    error: `The requested words "${requestedWords.join(' ')}" overlap with an existing overlay. The existing overlay covers these words: "${affectedWords.join(' ')}". Please choose a different word range that doesn't overlap with existing overlays.`,
                };
            }
        }

        return { startTimeMillis, endTimeMillis };
    },
);

/**
 * Re-time an overlay to cover exactly `[startTimeMillis, endTimeMillis]` (transcript time), keeping
 * its identity/content. Clears other overlays from the region, then moves both edges. Growing over a
 * neighbour's words pushes that neighbour back; shrinking leaves the freed words uncovered (the
 * waveform base shows through) rather than stretching a neighbour to fill. This is deliberately
 * non-sticky for every voice-driven type: the agent edits explicitly, unlike the human drag handles
 * (OverlayDragHandlePlugin's `handlesAreSticky`), which stay sticky for voiceover. Edges move
 * end-first when growing so the scene never collapses to zero (which deletes it).
 */
export const setSceneRangeOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group missing');
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('Scene missing');
        }
        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) {
            throw new Error('Base scene design id not found');
        }

        const state = getState();
        const currentEntry = partitionTranscriptByScene(state, sceneGroup)?.find(
            (entry) => entry.scene.id === sceneId,
        );
        const currentEndMillis = currentEntry?.endMillis;
        const growingEnd = currentEndMillis === undefined || endTimeMillis > currentEndMillis;

        const moveStart = () =>
            call(
                translateOverlayStartOperation,
                sceneId,
                sceneGroupId,
                baseSceneDesignId,
                startTimeMillis,
                systemTriggered,
                parentActionGroup,
            );
        const moveEnd = () =>
            call(
                translateOverlayEndOperation,
                sceneId,
                sceneGroupId,
                baseSceneDesignId,
                endTimeMillis,
                systemTriggered,
                parentActionGroup,
            );

        call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
            call(
                moveOverlaysAwayFromTimesOperation,
                sceneGroupId,
                startTimeMillis,
                endTimeMillis,
                [sceneId],
                systemTriggered,
                parentActionGroup,
            );
            if (growingEnd) {
                moveEnd();
                moveStart();
            } else {
                moveStart();
                moveEnd();
            }
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
